import { ORDEM_TRIAGEM } from '../didatico/ocupantes'
import { atualizarObjeto } from './operacoes'
import type { CenaTabletop, ObjetoTabletop } from './tipos'

/**
 * Uma vítima na fila de atendimento da cena inteira: de qualquer veículo, ou
 * do solo. `posicao` é 1-based (1º, 2º…), a leitura que aparece na cena e no
 * painel.
 */
export interface ItemFilaAtendimento {
  readonly posicao: number
  readonly objeto: ObjetoTabletop
}

/**
 * Ordena todas as vítimas da cena — de todos os veículos e do solo — por
 * quem sai primeiro: cor de triagem, depois `prioridadeManual` (menor sai
 * antes), depois a ordem em que entraram na cena (empate estável).
 *
 * É a fila que responde "quem sai primeiro" quando há vários carros e
 * várias vítimas — o `EstadoClinico` binário de `ocupantes.ts` não separa,
 * por exemplo, duas vítimas vermelhas em carros diferentes.
 */
export function filaDeAtendimento(objetos: readonly ObjetoTabletop[]): ItemFilaAtendimento[] {
  return objetos
    .map((objeto, ordem) => ({ objeto, ordem }))
    .filter((item): item is { objeto: ObjetoTabletop & { vitima: NonNullable<ObjetoTabletop['vitima']> }; ordem: number } =>
      item.objeto.vitima !== undefined,
    )
    .sort((a, b) => {
      const porTriagem = ORDEM_TRIAGEM[a.objeto.vitima.triagem] - ORDEM_TRIAGEM[b.objeto.vitima.triagem]
      if (porTriagem !== 0) return porTriagem
      const manualA = a.objeto.vitima.prioridadeManual ?? Number.POSITIVE_INFINITY
      const manualB = b.objeto.vitima.prioridadeManual ?? Number.POSITIVE_INFINITY
      if (manualA !== manualB) return manualA - manualB
      return a.ordem - b.ordem
    })
    .map(({ objeto }, indice) => ({ posicao: indice + 1, objeto }))
}

/** A posição (1-based) de uma vítima na fila, ou `null` se ela não estiver na cena. */
export function posicaoNaFila(objetos: readonly ObjetoTabletop[], idVitima: string): number | null {
  const item = filaDeAtendimento(objetos).find(({ objeto }) => objeto.id === idVitima)
  return item?.posicao ?? null
}

/**
 * Troca a ordem de duas vítimas ADJACENTES na fila (o botão ↑/↓ do painel),
 * ajustando `prioridadeManual` das duas para o desempate ficar explícito
 * mesmo que elas tenham a mesma cor de triagem.
 *
 * Sem efeito se `idVitima` já for a primeira (subir) ou a última (descer) da
 * fila, ou se ela não existir.
 */
export function moverNaFila(
  cena: CenaTabletop,
  idVitima: string,
  direcao: 'subir' | 'descer',
): CenaTabletop {
  const fila = filaDeAtendimento(cena.objetos)
  const indice = fila.findIndex(({ objeto }) => objeto.id === idVitima)
  if (indice === -1) return cena

  const indiceVizinho = direcao === 'subir' ? indice - 1 : indice + 1
  if (indiceVizinho < 0 || indiceVizinho >= fila.length) return cena

  const atual = fila[indice]!
  const vizinho = fila[indiceVizinho]!
  // as duas trocam de prioridadeManual — isso as reordena mesmo quando a cor
  // de triagem é igual, e continua respeitando quem tem triagem mais grave
  // (a comparação de cor sempre vem antes, em filaDeAtendimento)
  const comAtual = atualizarObjeto(cena, atual.objeto.id, {
    vitima: { prioridadeManual: vizinho.posicao },
  })
  return atualizarObjeto(comAtual, vizinho.objeto.id, {
    vitima: { prioridadeManual: atual.posicao },
  })
}
